import { clamp, distance } from '../match/Vector2';
import { PITCH_GEOMETRY } from '../match/PitchGeometry';
import type { ActionCandidate, UtilityBreakdown, UtilityContribution } from './ActionCandidate';
import type { PlayerDecisionContext } from './PlayerDecisionContext';
import { ElementSystem } from './ElementSystem';
import { PassiveSystem } from './PassiveSystem';
import { roleFamily } from './RoleProfiles';
import { TacticalRoleSystem } from './TacticalRoleSystem';

export class UtilityScorer {
  constructor(
    private readonly elements?: ElementSystem,
    private readonly passives = new PassiveSystem(),
    private readonly roles = new TacticalRoleSystem(),
  ) {}

  score(candidate: ActionCandidate, context: PlayerDecisionContext, knownRepetitionPenalty?: number): number {
    return this.evaluate(candidate, context, knownRepetitionPenalty).finalScore;
  }

  evaluate(candidate: ActionCandidate, context: PlayerDecisionContext, knownRepetitionPenalty?: number): UtilityBreakdown {
    const contributions = this.contributions(candidate, context);
    let contextualScore = contributions.reduce((total, contribution) => total + contribution.value, 0);
    const antiLoopPenalty = knownRepetitionPenalty ?? this.repetitionPenalty(candidate, context);
    const weights = context.state.config.utilityAi;
    const actor = context.actor;
    const technique = candidate.techniqueId
      ? actor.techniques.find((item) => item.id === candidate.techniqueId)
      : undefined;
    const assignedRole = actor.positionType === 'GK' ? null : actor.tacticalProfile.assignedRole;
    const role = this.roles.actionBias(
      assignedRole,
      candidate.type,
      candidate.passType,
      technique?.type,
      actor.tacticalProfile.roleFamiliarity ?? 1,
    );
    if (role.multiplier !== 1) {
      const before = contextualScore;
      contextualScore *= role.multiplier;
      contributions.push({ key: 'role.multiplier', value: contextualScore - before });
    }

    const roleRisk = this.roles.riskBias(
      assignedRole,
      candidate.riskScore,
      actor.tacticalProfile.roleFamiliarity ?? 1,
    );
    if (roleRisk.multiplier !== 1) {
      const before = contextualScore;
      contextualScore *= roleRisk.multiplier;
      contributions.push({ key: 'role.riskTolerance', value: contextualScore - before });
    }

    const opponent = this.directOpponent(candidate, context);
    const elementalModifier = this.elementalMultiplier(candidate, context, opponent, technique);
    if (elementalModifier !== 1) {
      const before = contextualScore;
      contextualScore *= elementalModifier;
      contributions.push({ key: 'element.multiplier', value: contextualScore - before });
    }

    const passive = this.passives.applyPassiveModifier({
      player: actor,
      opponent,
      actionType: candidate.type,
      baseScore: contextualScore,
      context: this.passiveContext(candidate, context),
      technique,
      elementMultiplier: elementalModifier,
    });
    if (passive.score !== contextualScore) {
      contributions.push({ key: 'passive.multiplier', value: passive.score - contextualScore });
      contextualScore = passive.score;
    }
    if (candidate.type === 'USE_TECHNIQUE' && passive.techniqueAggressionMultiplier !== 1) {
      const before = contextualScore;
      contextualScore *= passive.techniqueAggressionMultiplier;
      contributions.push({ key: 'passive.techniqueAggression', value: contextualScore - before });
    }
    const rawScore = contributions.reduce((total, contribution) => total + contribution.value, 0);
    const roleReasons = [...role.reasons, ...roleRisk.reasons];
    const debugReasons = [...roleReasons, ...passive.debugReasons];
    if (elementalModifier !== 1) debugReasons.push(`Elemento ×${elementalModifier.toFixed(2)}`);
    return {
      contributions,
      rawScore,
      antiLoopPenalty,
      finalScore: clamp(rawScore - antiLoopPenalty, weights.scoreMinimum, weights.scoreMaximum),
      assignedRole,
      passive: actor.passive,
      roleBiasesApplied: roleReasons,
      passiveModifiersApplied: passive.debugReasons,
      elementalModifier,
      debugReasons,
      staminaCostMultiplier: passive.staminaCostMultiplier,
      foulRiskMultiplier: passive.foulRiskMultiplier,
      techniqueAggressionMultiplier: passive.techniqueAggressionMultiplier,
    };
  }

  repetitionPenalty(candidate: ActionCandidate, context: PlayerDecisionContext): number {
    const recent = context.state.recentActions[context.actor.teamSide];
    const weights = context.state.config.utilityAi.repetition;
    let penalty = 0;
    for (let index = 0; index < recent.length; index++) {
      const action = recent[index];
      if (action.type !== candidate.type) continue;
      const recency = (index + 1) / recent.length;
      if (action.actorId === candidate.actorId && action.targetId === candidate.targetPlayerId) {
        penalty += weights.sameActionTargetPenaltyWeight * recency;
      }
      if (candidate.type === 'PASS' && action.targetId === candidate.targetPlayerId) {
        penalty += weights.samePassTargetPenaltyWeight * recency;
      }
    }
    const previous = recent.at(-1);
    if (candidate.type === 'PASS' && previous?.type === 'PASS'
      && previous.actorId === candidate.targetPlayerId && previous.targetId === candidate.actorId) {
      penalty += weights.immediateReturnPenalty;
    }
    if (candidate.type === 'PASS' && candidate.targetPlayerId) {
      const pairUses = recent.filter((action) => action.type === 'PASS' && action.targetId
        && this.samePair(action.actorId, action.targetId, candidate.actorId, candidate.targetPlayerId!)).length;
      if (pairUses >= context.state.config.repeatedPassPairLimit) {
        penalty += context.state.config.repeatedPassPairPenalty
          * (pairUses - context.state.config.repeatedPassPairLimit + 1);
      }
      if (this.isPossessionStagnant(context)) {
        const targetX = candidate.targetPosition?.x ?? context.actor.position.x;
        const targetProgress = PITCH_GEOMETRY.relativeProgress(context.actor.teamSide, targetX);
        // Once the reset has used two short, non-progressive passes, the next
        // pass must genuinely break a line. Letting it add only the remaining
        // centimetres permits visually stagnant four-pass sequences.
        const requiredProgress = Math.min(100, (context.sequenceStartProgress ?? PITCH_GEOMETRY.relativeProgress(
          context.actor.teamSide,
          context.actor.position.x,
        )) + context.state.config.stagnationMinimumProgress);
        if (targetProgress < requiredProgress) {
          penalty += context.state.config.stagnationNonProgressivePenalty;
        }
      }
    }
    const prospective = [...recent, {
      actorId: candidate.actorId,
      type: candidate.type,
      targetId: candidate.targetPlayerId,
      second: context.state.time,
    }];
    if (this.closesRepeatedCycle(prospective)) penalty += context.state.config.repeatedActionCyclePenalty;
    return penalty;
  }

  private contributions(candidate: ActionCandidate, context: PlayerDecisionContext): UtilityContribution[] {
    const actor = context.actor;
    const config = context.state.config.utilityAi;
    const result: UtilityContribution[] = [];
    const add = (key: string, value: number): void => { result.push({ key, value }); };
    const target = candidate.targetPlayerId
      ? context.teammates.find((player) => player.playerId === candidate.targetPlayerId)
      : undefined;

    switch (candidate.type) {
      case 'PASS': {
        const weights = config.pass;
        const direction = actor.teamSide === 0 ? 1 : -1;
        const targetX = candidate.targetPosition?.x ?? actor.position.x;
        const forwardGain = candidate.passingLane?.forwardGain ?? (targetX - actor.position.x) * direction;
        const recycleUnderPressure = actor.positionType === 'FW' && context.fieldZone === 'FINAL'
          && context.pressureLevel >= .4 && target?.intent.reason === 'RECYCLE_ATTACK';
        add('attributes.pass', actor.stats.pass * weights.actorPassWeight);
        add('attributes.control', actor.stats.control * weights.actorControlWeight);
        add('receiver.control', (target?.stats.control ?? weights.receiverFallbackStat) * weights.receiverControlWeight);
        add('receiver.speed', (target?.stats.speed ?? weights.receiverFallbackStat) * weights.receiverSpeedWeight);
        add('tactical.position', this.tacticalBonus(candidate, context) * weights.tacticalWeight);
        add('intent.run', target?.movementState === 'MAKING_RUN'
          ? weights.makingRunBonus
          : target?.movementState === 'SUPPORTING_ATTACK' || target?.movementState === 'TRACKING_RUN'
            ? weights.supportingRunBonus : 0);
        add('momentum', context.momentum * weights.momentumWeight);
        add('team.tempo', (context.teamPlan.tempo - 0.5) * config.teamTactics.passTempoWeight);
        add('team.phase.buildUp', context.teamPlan.phase === 'BUILD_UP' ? config.teamTactics.buildUpPassBonus : 0);
        add('team.phase.progression', context.teamPlan.phase === 'PROGRESSION' ? config.teamTactics.progressionPassBonus : 0);
        add('progression', clamp(forwardGain * weights.forwardGainWeight, weights.forwardIntentMinimum, weights.forwardIntentMaximum));
        add('penalty.backward', forwardGain < -1 && !recycleUnderPressure
          ? -Math.min(weights.backwardMaximumPenalty, weights.backwardBasePenalty + Math.abs(forwardGain) * weights.backwardDistanceWeight)
          : 0);
        // A team may recycle under pressure, but an unpressed, flat pass in
        // midfield should not beat a clear carrying lane forever. This is
        // deliberately softer than the hard anti-loop penalties below: it
        // changes the attacking preference without banning a safe outlet.
        add('penalty.passiveRecycle', context.fieldZone !== 'DEFENSIVE'
          && context.pressureLevel < .35 && forwardGain < 4 && !recycleUnderPressure
          ? -Math.min(18, (4 - forwardGain) * 2.2) : 0);
        // A visible passing option must not automatically erase an open run
        // with the ball. In midfield, prefer the short carry when it can gain
        // a line and the proposed pass merely moves possession a few metres.
        // Purposeful through balls and an actual attacking run remain more
        // valuable than forcing a solo action.
        const hasCarryLane = actor.positionType === 'MF'
          && context.fieldZone === 'MIDDLE'
          && context.perception.localSpace >= config.dribble.carrySpaceThreshold
          && this.hasOpenCarryLane(context, config.dribble.openLaneDepth, config.dribble.openLaneHalfWidth);
        const isPurposefulPass = candidate.passType === 'THROUGH' || target?.movementState === 'MAKING_RUN';
        const carryLanePenalty = hasCarryLane && !isPurposefulPass && !recycleUnderPressure
          && forwardGain < weights.carryLanePassProgressThreshold
          ? forwardGain < 5 ? weights.carryLaneShortPassPenalty : weights.carryLaneModeratePassPenalty
          : 0;
        add('penalty.openCarryLane', -carryLanePenalty);
        add('penalty.finalThirdRecycle', context.fieldZone === 'FINAL' && forwardGain <= 0 && !recycleUnderPressure
          ? -weights.finalThirdBackwardPenalty : 0);
        add('penalty.strikerToDefender', actor.positionType === 'FW' && target?.positionType === 'DF'
          && context.fieldZone === 'FINAL' ? -weights.strikerToDefenderPenalty : 0);
        add('penalty.goalkeeperRecycle', target?.positionType === 'GK' && context.fieldZone !== 'DEFENSIVE'
          ? -weights.goalkeeperRecyclePenalty : 0);
        add('penalty.shootingZoneRecycle', actor.positionType === 'FW' && context.distanceToGoal <= 28 && !recycleUnderPressure
          && forwardGain < 4 ? -weights.shootingZoneRecyclePenalty : 0);
        const targetRole = target ? roleFamily(target.role, target.positionType) : undefined;
        const linksAdjacentLine = (actor.positionType === 'GK' && target?.positionType === 'DF')
          || (actor.positionType === 'DF' && target?.positionType === 'MF')
          || (actor.positionType === 'MF' && (target?.positionType === 'MF'
            || targetRole === 'FULL_BACK' || targetRole === 'WING_BACK'));
        add('buildUp.adjacentLine', linksAdjacentLine ? weights.buildUpLinkBonus : 0);
        add('buildUp.fullBackOutlet', (targetRole === 'FULL_BACK' || targetRole === 'WING_BACK')
          && actor.positionType !== 'FW' && context.fieldZone !== 'FINAL' ? weights.fullBackOutletBonus : 0);
        add('attack.throughBall', candidate.passType === 'THROUGH' ? weights.throughBallBonus : 0);
        add('attack.purposefulLongPass', candidate.passType !== 'LOB' && candidate.passingLane
          && candidate.passingLane.distance >= 28 && forwardGain >= 10 ? weights.purposefulLongPassBonus : 0);
        add('attack.recycleSupport', recycleUnderPressure ? weights.recycleSupportBonus : 0);
        add('penalty.buildUpDirectForward', actor.positionType === 'DF' && target?.positionType === 'FW'
          && (context.teamPlan.phase === 'BUILD_UP' || context.teamPlan.phase === 'PROGRESSION')
          ? -weights.directForwardBuildUpPenalty : 0);
        add('perception.pressure', -context.perception.pressure * weights.pressurePenaltyWeight);
        add('perception.passingLaneRisk', -(candidate.passingLane?.risk ?? candidate.riskScore) * weights.riskPenaltyWeight);
        break;
      }
      case 'DRIBBLE':
      case 'CARRY': {
        const weights = config.dribble;
        add('attributes.dribble', actor.stats.dribble * weights.dribbleWeight);
        add('attributes.control', actor.stats.control * weights.controlWeight);
        add('attributes.speed', actor.stats.speed * weights.speedWeight);
        add('attributes.power', actor.stats.power * weights.powerWeight);
        add('momentum', context.momentum * weights.momentumWeight);
        add('team.risk', (context.teamPlan.risk - 0.5) * config.teamTactics.dribbleRiskWeight);
        add('team.phase.transition', context.teamPlan.phase === 'ATTACKING_TRANSITION'
          ? config.teamTactics.transitionDribbleBonus : 0);
        add('stamina.deficit', -(100 - actor.staminaCurrent) * weights.staminaDeficitPenaltyWeight);
        add('perception.pressure', -context.perception.pressure * weights.pressurePenaltyWeight);
        add('perception.localSpace', context.perception.localSpace * weights.localSpaceBonusWeight);
        add('zone.finalThird', context.fieldZone === 'FINAL' && context.distanceToGoal <= 32 ? weights.finalThirdBonus : 0);
        add('penalty.patience', -(context.fieldZone === 'DEFENSIVE'
          ? weights.defensivePatiencePenalty
          : context.fieldZone === 'MIDDLE' ? weights.middlePatiencePenalty : weights.finalPatiencePenalty));
        add('stagnation.breakLine', this.isPossessionStagnant(context) ? context.state.config.stagnationDribbleBonus : 0);
        const actorRole = roleFamily(actor.role, actor.positionType);
        const hasCarrySpace = context.perception.localSpace >= weights.carrySpaceThreshold;
        const openLane = this.hasOpenCarryLane(context, weights.openLaneDepth, weights.openLaneHalfWidth);
        const favourableDuel = this.hasFavourableDribbleDuel(context, weights.favorableDribbleMargin);
        const carrySkill = this.midfielderCarrySkill(actor);
        // Rather than treating one arbitrary rating as a binary switch,
        // progressively reward midfielders who can protect the ball. A
        // competent midfielder can still advance into a genuinely empty lane;
        // specialists do it more decisively.
        const carryReadiness = clamp((carrySkill - weights.midfielderCarrySkillThreshold + 12) / 24, 0, 1);
        const carryProgress = candidate.targetPosition
          ? Math.max(0, PITCH_GEOMETRY.relativeProgress(actor.teamSide, candidate.targetPosition.x)
            - PITCH_GEOMETRY.relativeProgress(actor.teamSide, actor.position.x))
          : 0;
        add('progression.carry', candidate.type === 'CARRY' && actor.positionType === 'MF'
          ? carryProgress * weights.carryProgressWeight : 0);
        add('role.midfieldCarry', actor.positionType === 'MF' && context.fieldZone !== 'FINAL' && hasCarrySpace
          ? weights.midfielderCarryBonus * carryReadiness : 0);
        // A midfielder should not behave as a pass-only relay. A free channel
        // ahead is more relevant than a marker who happens to be behind or to
        // the side, while a clear technical edge makes a direct dribble worth
        // attempting when a defender is actually in range.
        add('role.midfieldOpenLane', actor.positionType === 'MF' && candidate.type === 'CARRY'
          && context.fieldZone !== 'FINAL' && openLane
          ? weights.midfielderOpenLaneCarryBonus * carryReadiness : 0);
        add('role.midfieldDirectDrive', actor.positionType === 'MF' && candidate.type === 'CARRY'
          && context.fieldZone === 'MIDDLE' && hasCarrySpace && openLane
          ? weights.midfielderDirectDriveBonus * carryReadiness : 0);
        add('role.midfieldFavourableDuel', actor.positionType === 'MF' && candidate.type === 'DRIBBLE'
          && context.fieldZone !== 'DEFENSIVE' && favourableDuel ? weights.midfielderFavorableDribbleBonus : 0);
        add('role.wideDefenderCarry', (actorRole === 'FULL_BACK' || actorRole === 'WING_BACK')
          && context.fieldZone !== 'FINAL' && hasCarrySpace ? weights.wideDefenderCarryBonus : 0);
        break;
      }
      case 'SHOOT': {
        const weights = config.shoot;
        add('base', weights.base);
        add('attributes.kick', actor.stats.kick * weights.kickWeight);
        add('attributes.control', actor.stats.control * weights.controlWeight);
        add('attributes.power', actor.stats.power * weights.powerWeight);
        add('momentum', context.momentum * weights.momentumWeight);
        add('team.risk', (context.teamPlan.risk - 0.5) * config.teamTactics.shootRiskWeight);
        add('team.mentality', (context.teamPlan.mentality - 0.5) * config.teamTactics.shootMentalityWeight);
        add('team.phase.finalThird', context.teamPlan.phase === 'FINAL_THIRD'
          ? config.teamTactics.finalThirdShootBonus : 0);
        add('distance', -context.perception.distanceToGoal * weights.distancePenaltyWeight);
        add('perception.pressure', -context.perception.pressure * weights.pressurePenaltyWeight);
        add('perception.goalAngle', context.perception.goalAngleQuality * weights.goalAngleBonusWeight);
        add('role', actor.positionType === 'FW' ? weights.forwardPositionBonus
          : actor.positionType === 'MF' ? weights.midfielderPositionBonus
            : actor.positionType === 'DF' ? weights.defenderPositionBonus : weights.goalkeeperPositionBonus);
        add('zone.finalThird', context.fieldZone === 'FINAL' ? weights.finalThirdBonus : 0);
        add('range.close', context.distanceToGoal <= 18
          ? weights.veryCloseRangeBonus : context.distanceToGoal <= 26 ? weights.closeRangeBonus : 0);
        add('finish.cross', actor.lastReceptionPassType === 'CROSS'
          && context.state.time - (actor.lastReceptionTime ?? -Infinity) <= context.state.config.crossFinishWindowSeconds
          ? weights.crossFinishBonus : 0);
        const finalThirdSequence = context.fieldZone === 'FINAL'
          && context.consecutiveTeamPasses >= context.state.config.stagnationPassLimit;
        add('finish.stagnation', finalThirdSequence && context.perception.goalAngleQuality >= .46
          ? weights.stagnationFinishBonus : 0);
        const closeDefenders = context.opponents.filter((opponent) => distance(opponent.position, actor.position) <= 8.5).length;
        add('penalty.congestedForward', actor.positionType === 'FW' && context.fieldZone === 'FINAL'
          && context.distanceToGoal > 7 && closeDefenders >= 2 ? -weights.congestedForwardShotPenalty : 0);
        break;
      }
      case 'CLEAR_BALL': {
        const weights = config.clearBall;
        add('base', weights.base);
        add('zone.defensive', context.fieldZone === 'DEFENSIVE' ? weights.defensiveZoneBonus : 0);
        add('perception.pressure', context.perception.pressure * weights.pressureBonusWeight);
        break;
      }
      case 'TACKLE': {
        const weights = config.tackle;
        add('attributes.defense', actor.stats.defense * weights.defenseWeight);
        add('attributes.power', actor.stats.power * weights.powerWeight);
        add('attributes.speed', actor.stats.speed * weights.speedWeight);
        add('attributes.control', actor.stats.control * weights.controlWeight);
        add('perception.pressure', context.perception.pressure * weights.pressureBonusWeight);
        add('team.pressing', (context.teamPlan.pressing - 0.5) * config.teamTactics.tacklePressingWeight);
        break;
      }
      case 'USE_TECHNIQUE': {
        const weights = config.technique;
        const technique = actor.techniques.find((item) => item.id === candidate.techniqueId);
        const baseType = technique?.type === 'SHOT' ? 'SHOOT'
          : technique?.type === 'PASS' ? 'PASS'
            : technique?.type === 'DRIBBLE' ? 'DRIBBLE'
              : technique?.type === 'BLOCK' ? 'TACKLE' : 'HOLD_POSITION';
        const baseCandidate = { ...candidate, type: baseType, techniqueId: undefined, tpCost: 0 } as ActionCandidate;
        // The outer technique candidate receives role, element and passive
        // modifiers once. Its embedded ordinary action contributes only the
        // underlying football score, preventing accidental double bonuses.
        const baseContributions = this.contributions(baseCandidate, context);
        for (const contribution of baseContributions) add(`baseAction.${contribution.key}`, contribution.value);
        const basePenalty = this.repetitionPenalty(baseCandidate, context);
        add('baseAction.antiLoopPenalty', -basePenalty);
        const representedBase = baseContributions.reduce((total, contribution) => total + contribution.value, 0) - basePenalty;
        add('baseAction.clamp', clamp(representedBase, config.scoreMinimum, config.scoreMaximum) - representedBase);
        add('technique.power', (technique?.power ?? 0) * weights.powerWeight);
        add('matchMoment', context.minute >= 80
          ? context.scoreDiff < 0 ? weights.lateLosingBonus
            : context.scoreDiff === 0 ? weights.lateDrawingBonus : weights.lateWinningBonus
          : context.scoreDiff < 0 ? weights.losingBonus : 0);
        add('technique.context', technique?.type === 'SHOT' && context.fieldZone === 'FINAL'
          ? weights.finalThirdShotBonus + (actor.positionType === 'FW' ? weights.forwardShotBonus : 0)
          : technique?.type === 'DRIBBLE' && context.fieldZone !== 'DEFENSIVE' ? weights.attackingDribbleBonus : 0);
        add('momentum', context.momentum * weights.momentumWeight);
        add('team.techniqueAggression', (context.teamPlan.techniqueAggression - 0.5) * config.teamTactics.techniqueAggressionWeight);
        add('tp.cost', -candidate.tpCost * weights.tpCostPenaltyWeight);
        const remainingTp = actor.tpCurrent - candidate.tpCost;
        add('tp.reserve', -(context.minute < 80 ? weights.earlyPreservePenalty : 0)
          - (remainingTp < actor.tpMax * 0.35
            ? context.minute >= 80 ? weights.lowReserveLatePenalty : weights.lowReservePenalty : 0)
          - (context.scoreDiff > 0 ? weights.winningReservePenalty : 0));
        break;
      }
      case 'HOLD_POSITION':
        add('base', config.holdPosition.base);
        add('stamina.recovery', actor.staminaCurrent < 25 ? config.holdPosition.tiredBonus : 0);
        break;
      default:
        add('base', config.fallbackScore);
    }
    return result;
  }

  private directOpponent(
    candidate: ActionCandidate,
    context: PlayerDecisionContext,
  ): PlayerDecisionContext['opponents'][number] | undefined {
    if (candidate.targetPlayerId) {
      const targeted = context.opponents.find((player) => player.playerId === candidate.targetPlayerId);
      if (targeted) return targeted;
    }
    if (candidate.type === 'SHOOT'
      || (candidate.type === 'USE_TECHNIQUE'
        && context.actor.techniques.find((item) => item.id === candidate.techniqueId)?.type === 'SHOT')) {
      return context.opponents.find((player) => player.positionType === 'GK');
    }
    if (!['DRIBBLE', 'TACKLE', 'INTERCEPT', 'PASS', 'USE_TECHNIQUE'].includes(candidate.type)) return undefined;
    return [...context.opponents].sort((first, second) => distance(first.position, context.actor.position)
      - distance(second.position, context.actor.position) || first.playerId.localeCompare(second.playerId))[0];
  }

  private hasOpenCarryLane(context: PlayerDecisionContext, depth: number, halfWidth: number): boolean {
    const actor = context.actor;
    const actorProgress = PITCH_GEOMETRY.relativeProgress(actor.teamSide, actor.position.x);
    return !context.opponents.some((opponent) => {
      const opponentProgress = PITCH_GEOMETRY.relativeProgress(actor.teamSide, opponent.position.x);
      const ahead = opponentProgress > actorProgress + 1 && opponentProgress <= actorProgress + depth;
      return ahead && Math.abs(opponent.position.y - actor.position.y) <= halfWidth;
    });
  }

  private midfielderCarrySkill(actor: PlayerDecisionContext['actor']): number {
    return actor.stats.dribble * .45 + actor.stats.control * .3 + actor.stats.speed * .25;
  }

  private hasFavourableDribbleDuel(context: PlayerDecisionContext, requiredMargin: number): boolean {
    const actor = context.actor;
    const defender = [...context.opponents]
      .filter((opponent) => distance(opponent.position, actor.position) <= 3.2)
      .sort((left, right) => distance(left.position, actor.position) - distance(right.position, actor.position)
        || left.playerId.localeCompare(right.playerId))[0];
    if (!defender) return false;
    const attack = actor.stats.dribble * .4 + actor.stats.control * .25 + actor.stats.speed * .15 + actor.stats.power * .1;
    const defense = defender.stats.defense * .4 + defender.stats.power * .2 + defender.stats.speed * .15 + defender.stats.control * .1;
    return attack >= defense + requiredMargin;
  }

  private elementalMultiplier(
    candidate: ActionCandidate,
    context: PlayerDecisionContext,
    opponent: PlayerDecisionContext['opponents'][number] | undefined,
    technique: PlayerDecisionContext['actor']['techniques'][number] | undefined,
  ): number {
    if (!opponent || !['DRIBBLE', 'CARRY', 'SHOOT', 'TACKLE', 'INTERCEPT', 'USE_TECHNIQUE'].includes(candidate.type)) return 1;
    const elements = this.elements ?? new ElementSystem(
      context.state.config.elementAdvantage,
      context.state.config.elementNeutral,
      context.state.config.elementDisadvantage,
    );
    return elements.getElementMultiplier(technique?.element ?? context.actor.element, opponent.element);
  }

  private passiveContext(candidate: ActionCandidate, context: PlayerDecisionContext) {
    const state = context.state;
    const counterStartedAt = state.counterWindowByTeam[context.actor.teamSide] - this.passives.config.counterWindowSeconds;
    const targetProgress = candidate.targetPosition
      ? PITCH_GEOMETRY.relativeProgress(context.actor.teamSide, candidate.targetPosition.x)
      : PITCH_GEOMETRY.relativeProgress(context.actor.teamSide, context.actor.position.x);
    const actorProgress = PITCH_GEOMETRY.relativeProgress(context.actor.teamSide, context.actor.position.x);
    return {
      minute: context.minute,
      scoreDiff: context.scoreDiff,
      secondsSincePossessionGain: state.possessionTeam === context.actor.teamSide
        && state.time <= state.counterWindowByTeam[context.actor.teamSide]
        ? state.time - counterStartedAt
        : Infinity,
      secondsSinceConceded: state.time - state.lastGoalConcededAtByTeam[context.actor.teamSide],
      isDecisive: candidate.type === 'SHOOT' || candidate.type === 'TACKLE'
        || candidate.type === 'INTERCEPT' || candidate.type === 'USE_TECHNIQUE'
        || context.minute >= 75 || Math.abs(context.scoreDiff) <= 1,
      isVerticalAction: targetProgress - actorProgress >= 4 || candidate.type === 'SHOOT' || candidate.type === 'MAKE_RUN',
    };
  }

  private isPossessionStagnant(context: PlayerDecisionContext): boolean {
    // The third short pass is the last permitted rebuilding touch. Starting
    // the penalty after the second non-progressive pass prevents a fourth
    // stationary exchange while still leaving room for a controlled reset.
    return context.consecutiveTeamPasses >= Math.max(2, context.state.config.stagnationPassLimit - 1)
      && context.recentTerritorialProgress < context.state.config.stagnationMinimumProgress;
  }

  private samePair(firstActor: string, firstTarget: string, secondActor: string, secondTarget: string): boolean {
    return (firstActor === secondActor && firstTarget === secondTarget)
      || (firstActor === secondTarget && firstTarget === secondActor);
  }

  private closesRepeatedCycle(actions: Array<{ actorId: string; type: string; targetId?: string }>): boolean {
    const signature = (action: { actorId: string; type: string; targetId?: string }) => `${action.type}:${action.actorId}:${action.targetId ?? ''}`;
    for (let length = 2; length <= Math.min(4, Math.floor(actions.length / 2)); length++) {
      const currentStart = actions.length - length;
      const previousStart = currentStart - length;
      let equal = true;
      for (let offset = 0; offset < length; offset++) {
        if (signature(actions[currentStart + offset]) !== signature(actions[previousStart + offset])) {
          equal = false;
          break;
        }
      }
      if (equal) return true;
    }
    return false;
  }

  private tacticalBonus(candidate: ActionCandidate, context: PlayerDecisionContext): number {
    const weights = context.state.config.utilityAi.tactical;
    if (!candidate.targetPosition) return weights.base;
    const direction = context.actor.teamSide === 0 ? 1 : -1;
    const forwardGain = (candidate.targetPosition.x - context.actor.position.x) * direction;
    const spacing = distance(context.actor.position, candidate.targetPosition);
    return clamp(
      weights.base + forwardGain * weights.forwardGainWeight
        - Math.abs(spacing - weights.idealSpacing) * weights.spacingPenaltyWeight,
      weights.minimum,
      weights.maximum,
    );
  }
}
